import axios from "axios";
import { ISentMailMessage } from "../Core/ViewModels/Message/SentMailMessageModel";
import { actionTypes as composeActions } from "../types/composeTypes";
import { actionTypes as notificationAction } from "../types/notificationTypes";
import { AppThunkAction } from "../store";
import {
    CompletedAddInEmailTemplateAction,
    CompletedAuthRetentions,
    CompletedComposeRecipients,
    CompletedRemoveAttachment,
    CompletedSendMessage,
    CompletedSetDefaultAuthentication,
    CompletePrimaryAdmin,
    ReceivedAddInEmailTemplateAction,
    ReceivedAuthRetentions,
    ReceivedComposeRecipients,
    ReceivedSendMessage,
    ReceivePrimaryAdmin,
    RequestAddInEmailTemplateAction,
    RequestAuthRetentions,
    RequestComposeRecipients,
    RequestPrimaryAdmin,
    RequestRemoveAttachment,
    RequestSendMessage,
    RequestSetDefaultAuthentication,
    RequestUploadAttachmentSasAction,
    SaveDocuments,
    SaveSentMessage,
} from "../actions/composeActions";
import { StatusType, NotificationAction } from "../store/Common/NotificationStore";
import { AuthenticationMethod } from "src/Core/Common/Enums";
import { IDocumentModel } from "src/components/Compose/IDocumentModel";
import { ISendOptionSettings } from "src/Core/Domain/Settings/MySettings";
import { IRetentionPeriod } from "src/Core/ViewModels/Company/Settings/RetentionSettings";
import { ISentMessageModel } from "src/Core/ViewModels/Message/SentMessageModel";
import { logger } from "src/routes/LoggedIn";
import { appInsightsData } from "src/helper/AppInsightsData";
import { ToasterMessages } from "src/helper/ToasterMessages";

export type DispatchActions =
    | RequestComposeRecipients
    | ReceivedComposeRecipients
    | CompletedComposeRecipients
    | RequestSendMessage
    | ReceivedSendMessage
    | CompletedSendMessage
    | RequestAuthRetentions
    | ReceivedAuthRetentions
    | CompletedAuthRetentions
    | RequestRemoveAttachment
    | CompletedRemoveAttachment
    | RequestUploadAttachmentSasAction
    | SaveDocuments
    | SaveSentMessage
    | RequestSetDefaultAuthentication
    | CompletedSetDefaultAuthentication
    | RequestAddInEmailTemplateAction
    | ReceivedAddInEmailTemplateAction
    | CompletedAddInEmailTemplateAction
    | RequestPrimaryAdmin
    | ReceivePrimaryAdmin
    | CompletePrimaryAdmin;

export type KnownActions = DispatchActions | NotificationAction;

export const fetchAuthRetentionPeriods =
    (authMethod: AuthenticationMethod, callback?: (data: IRetentionPeriod[]) => void): AppThunkAction<KnownActions> =>
        dispatch => {
            dispatch({
                type: composeActions.REQUEST_AUTH_RETENTIONS,
            });

            axios
                .get(
                    `${process.env.REACT_APP_API_URL}/api/ComposeMessage/AuthMethodRetentionPeriods?authMethod=${authMethod}`
                )
                .then(response => {
                    const data = response.data;
                    dispatch({
                        type: composeActions.RECEIVED_AUTH_RETENTIONS,
                        payload: data,
                    });
                    if (callback) {
                        callback(data);
                    }
                })
                .catch((error: Error) => {
                    dispatch({
                        type: notificationAction.NOTIFICATION,
                        statusMessage: error.message,
                        statusType: StatusType.Error,
                    });
                })
                .finally(() => {
                    dispatch({
                        type: composeActions.COMPLETED_AUTH_RETENTIONS,
                    });
                });
        };

export const fetchComposeRecipients =
    (searchString: string, callback?: (data: any) => void): AppThunkAction<KnownActions> =>
        dispatch => {
            dispatch({
                type: composeActions.REQUEST_COMPOSE_RECIPIENTS,
            });

            axios
                .get(`${process.env.REACT_APP_API_URL}/api/ComposeMessage/GetRecipients?filter=${searchString}`)
                .then(response => {
                    const data = response.data;
                    dispatch({
                        type: composeActions.RECEIVED_COMPOSE_RECIPIENTS,
                        payload: data,
                    });
                    if (callback) {
                        callback(data);
                    }
                })
                .catch((error: Error) => {
                    dispatch({
                        type: notificationAction.NOTIFICATION,
                        statusMessage: error.message,
                        statusType: StatusType.Error,
                    });
                })
                .finally(() => {
                    dispatch({
                        type: composeActions.COMPLETED_COMPOSE_RECIPIENTS,
                    });
                });
        };

export const sendMessage =
    (sentMailMessage: ISentMailMessage,
        callback?: (result: string) => void,
        errorCallback?: () => void): AppThunkAction<KnownActions> =>
        dispatch => {
            dispatch({
                type: composeActions.REQUEST_SEND_MESSAGE,
            });
            logger.trackEvent({
                name: `${appInsightsData.Compose.pageTitle} - ${appInsightsData.Compose.Operation.FinishButtonClick}`,
                properties: {
                    page: appInsightsData.Compose.pageTitle,
                    ...sentMailMessage
                }
            });
            axios
                .post(`${process.env.REACT_APP_API_URL}/api/ComposeMessage/SendMessageAndGenerateLink`, sentMailMessage)
                .then((response: any) => {
                    const data = response.data;
                    dispatch({
                        type: composeActions.RECEIVED_SEND_MESSAGE,
                        payload: data,
                    });
                    if (callback) {
                        callback(data);
                    }
                })
                .catch((error: Error) => {
                    errorCallback && errorCallback();
                })
                .finally(() => {
                    dispatch({
                        type: composeActions.COMPLETED_SEND_MESSAGE,
                    });
                });
        };

export const fetchUploadAttachmentSasLink =
    (documentGuid: string, callback?: (data: any) => void): AppThunkAction<KnownActions> =>
        dispatch => {
            axios
                .get(
                    `${process.env.REACT_APP_API_URL}/api/ComposeMessage/GetDocumentUploadLink?documentGuid=${documentGuid}`
                )
                .then(response => {
                    if (response.status == 200) {
                        const result = response.data;
                        const data = {
                            guid: documentGuid,
                            sas: result,
                        };
                        callback && callback(data);
                    }
                    else {
                        dispatch({
                            type: notificationAction.NOTIFICATION,
                            statusMessage: ToasterMessages.ERROR.ATTACHMENT_UPLOAD_FAILED,
                            statusType: StatusType.Error,
                        });
                    }
                })
                .catch(function () {
                    dispatch({
                        type: notificationAction.NOTIFICATION,
                        statusMessage: ToasterMessages.ERROR.ATTACHMENT_UPLOAD_FAILED,
                        statusType: StatusType.Error,
                    });
                });
        };

export const removeAttachment =
    (documentGuid: string, callback?: (guid: string) => void): AppThunkAction<KnownActions> =>
        dispatch => {
            dispatch({ type: composeActions.REQUEST_REMOVE_ATTACHMENT });
            logger.trackEvent({
                name: `${appInsightsData.Compose.pageTitle} - ${appInsightsData.Compose.Operation.AttachmentRemoved}`,
                properties: {
                    page: appInsightsData.Compose.pageTitle,
                    documentGuid
                }
            });
            axios
                .delete(`${process.env.REACT_APP_API_URL}/api/ComposeMessage/DeleteAttachment?documentGuid=${documentGuid}`)
                .then(() => {
                    dispatch({ type: composeActions.COMPLETED_REMOVE_ATTACHMENT, payload: documentGuid });
                    callback && callback(documentGuid);
                })
                .catch(function (error: Error) {
                    dispatch({
                        type: notificationAction.NOTIFICATION,
                        statusMessage: error.message,
                        statusType: StatusType.Error,
                    });
                })
                .finally(() => {
                    dispatch({ type: composeActions.COMPLETED_REMOVE_ATTACHMENT });
                });
        };

export const saveDocuments =
    (data: IDocumentModel[], callback?: () => void): AppThunkAction<KnownActions> =>
        dispatch => {
            dispatch({
                type: composeActions.SAVE_DOCUMENTS,
                payload: data,
            });
            if (callback) {
                callback();
            }
        };

export const saveSentMessageToStore =
    (data: ISentMessageModel): AppThunkAction<KnownActions> =>
        dispatch => {
            dispatch({
                type: composeActions.SAVE_SENT_MESSAGE,
                payload: data,
            });
        };
export const setDefaultAuthMethod =
    (
        authMethod: AuthenticationMethod,
        callback?: (sendOpSettings: ISendOptionSettings) => void
    ): AppThunkAction<KnownActions> =>
        dispatch => {
            dispatch({
                type: composeActions.REQUEST_SET_DEFAULT_AUTHENTICATION,
            });
            logger.trackEvent({
                name: `${appInsightsData.Compose.AuthMethod.pageTitle} - ${appInsightsData.Compose.Operation.DefaultAuthMethodChanged}`,
                properties: {
                    page: appInsightsData.Compose.AuthMethod.pageTitle,
                    authMethod
                }
            });
            axios
                .put(`${process.env.REACT_APP_API_URL}/api/ComposeMessage/SetDefaultAuthMethod/${authMethod}`)
                .then(response => {
                    const data = response.data;
                    dispatch({
                        type: composeActions.COMPLETED_SET_DEFAULT_AUTHENTICATION,
                    });
                    if (callback) callback(data);
                })
                .catch((error: Error) => {
                    dispatch({
                        type: notificationAction.NOTIFICATION,
                        statusMessage: error.message,
                        statusType: StatusType.Error,
                    });
                })
                .finally(() => {
                    dispatch({
                        type: composeActions.COMPLETED_SET_DEFAULT_AUTHENTICATION,
                    });
                });
        };

export const fetchAddInEmailTemplate =
    (templateName: string, callback?: (result: string) => void): AppThunkAction<KnownActions> =>
        dispatch => {
            dispatch({
                type: composeActions.REQUEST_ADDIN_EMAIL_TEMPLATE,
            });

            axios
                .get(
                    `${process.env.REACT_APP_API_URL}/api/ComposeMessage/GetAddInEmailTemplate?templateName=${templateName}`
                )
                .then(response => {
                    const result = response.data;
                    dispatch({
                        type: composeActions.RECEIVED_ADDIN_EMAIL_TEMPLATE,
                        payload: result,
                    });
                    if (callback) callback(result);
                })
                .catch((error: Error) => {
                    dispatch({
                        type: notificationAction.NOTIFICATION,
                        statusMessage: error.message,
                        statusType: StatusType.Error,
                    });
                })
                .finally(() => {
                    dispatch({
                        type: composeActions.COMPLETED_ADDIN_EMAIL_TEMPLATE,
                    });
                });
        };

export const fetchPrimaryAdmin =
    (callback?: any): AppThunkAction<KnownActions> =>
        dispatch => {
            dispatch({
                type: composeActions.REQUEST_PRIMARY_ADMIN,
            });
            axios
                .get(`${process.env.REACT_APP_API_URL}/api/ComposeMessage/GetPrimaryAdminForCompany`)
                .then((response: any) => {
                    const data = response.data;
                    dispatch({
                        type: composeActions.RECEIVE_PRIMARY_ADMIN,
                    });
                    callback(data);
                })
                .catch(function (error: any) {
                    dispatch({
                        type: notificationAction.NOTIFICATION,
                        statusMessage: error.message,
                        statusType: StatusType.Error,
                    });
                    dispatch({
                        type: composeActions.COMPLETE_PRIMARY_ADMIN,
                    });
                });
        };